import { native } from '@forest/native-services'
import {
  failureOf,
  tagFailure,
  withFailure,
  type PageLoadStep,
} from '@forest/paywall-core'
import { isHTTPError } from 'ky'
import { isConnectivityError } from '~/lib/is-connectivity-error'
import { queryClient } from '~/lib/query-client'
import { m } from '~/paraglide/messages'
import { progressesQueryKey } from '~/resources/progresses'
import { closeDialog, showDialog } from './dialog'

function showConnectionIssueDialog({ onRetry }: { onRetry: () => void }) {
  showDialog({
    title: m.dialog_connection_issue_title(),
    description: m.dialog_connection_issue_description(),
    primaryButtonText: m.retry(),
    secondaryButtonText: m.cancel(),
    primaryAction: onRetry,
    secondaryAction: () => closeDialog(),
  })
}

function showUnexpectedErrorDialog({ code }: { code: number | string }) {
  showDialog({
    title: m.dialog_error_occur_title(),
    description: m.dialog_error_occur_description({ status: code }),
    primaryButtonText: m.OK(),
    primaryAction: () => closeDialog(),
  })
}

/** 4xx on /reward typically means the progress state was stale; refetch and
 *  dismiss silently. Persistent failures escalate to a connection-issue
 *  dialog so the user isn't stuck on silent breakage. */
export function handleStaleRewardError(args: {
  retryCount: number
  onRetry: () => void
}) {
  queryClient.invalidateQueries({ queryKey: progressesQueryKey })
  if (args.retryCount > 0) {
    showConnectionIssueDialog({ onRetry: args.onRetry })
  }
}

let dataLoadFailDialogShown = false

/** Terminal "Fail to load" dialog matching focus-pass for initial-load
 *  failures. Logs a fatal error for observability and closes the WebView on
 *  OK. Guarded so concurrent query failures don't stack/flicker dialogs.
 *  The failure code names `step` unless the error was already tagged with a
 *  narrower one (ADR 0037). */
export function showDataLoadFailDialog(args: {
  source: string
  step?: PageLoadStep
  error?: unknown
}): void {
  if (dataLoadFailDialogShown) return
  dataLoadFailDialogShown = true
  if (args.step) tagFailure(args.error, args.step)
  const failure = failureOf(args.error, 'load')
  native.general.logFatalError({
    name: args.source,
    // The payload has no params; the code leads the free-text reason.
    reason: `${failure.code} | ${String(args.error)}`,
  })
  showDialog({
    title: m.dialog_data_load_fail_title(),
    description:
      withFailure(m.dialog_data_load_fail_description(), failure) +
      (import.meta.env.MODE === 'production' ? '' : `\n${String(args.error)}`),
    primaryButtonText: m.ok(),
    primaryAction: () => native.general.close(),
  })
}

/** Generic dispatch driven by the classifier. Use this when a mutation has
 *  no domain-specific 422 handling and just needs the standard ladder. */
export function dispatchGenericError(
  error: unknown,
  onRetry: () => void,
): void {
  if (isConnectivityError(error)) {
    showConnectionIssueDialog({ onRetry })
    return
  }
  if (isHTTPError(error)) {
    showUnexpectedErrorDialog({ code: error.response.status })
    return
  }
  showUnexpectedErrorDialog({ code: String(error) })
}
